import React from 'react';
import { PieChart, Pie, Cell, Tooltip, ResponsiveContainer } from 'recharts';

const DEFAULT_COLORS = [
  '#4e79a7',
  '#f28e2b',
  '#e15759',
  '#76b7b2',
  '#59a14f',
  '#bab0ab',
];

/**
 * DonutChart — 자동 탑N+기타 그룹화 도넛 차트.
 * 사용법: docs/modules/frontend/DonutChart.md
 */
const DonutChart = ({ data, title, width = 400, height = 300, topN }) => {
  const safeData = Array.isArray(data) ? data : [];

  if (safeData.length === 0) {
    return (
      <div style={{ width, height, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <span>표시할 데이터가 없습니다</span>
      </div>
    );
  }

  // 1) value 내림차순 정렬
  const sorted = [...safeData].sort((a, b) => b.value - a.value);

  // 2) topN 명시 시에만 클라이언트 측 탑N 재처리.
  //    topN 미지정 → 받은 데이터(정렬만) 그대로 렌더 (backend 가 이미 탑N+기타 가공한 경우 = 단일 진실 소스).
  //    topN 초과는 "기타" 1조각으로 합산.
  //    (j) head 안에 이미 label === '기타' 항목이 있으면 그 항목에 etcValue 를 합산
  //        (새 "기타" 조각을 또 추가하지 않음 → "기타" 조각 항상 ≤ 1개, 수치 보존).
  let chartData;
  if (Number.isFinite(topN) && topN > 0 && sorted.length > topN) {
    const head = sorted.slice(0, topN);
    const tail = sorted.slice(topN);
    const etcValue = tail.reduce((s, d) => s + d.value, 0);
    if (etcValue === 0) {
      chartData = head;
    } else {
      const existingEtc = head.find((d) => d.label === '기타');
      if (existingEtc) {
        chartData = head.map((d) =>
          d === existingEtc ? { ...d, value: d.value + etcValue } : d
        );
      } else {
        chartData = [...head, { label: '기타', value: etcValue }];
      }
    }
  } else {
    chartData = sorted;
  }

  return (
    <div style={{ width, height }}>
      {title ? <div style={{ textAlign: 'center', fontWeight: 600 }}>{title}</div> : null}
      {/* 테스트 가시성을 위한 마커 (jsdom 에서 PieChart svg 가 렌더되어도 활용) */}
      <div style={{ display: 'none' }} aria-hidden="true">
        {chartData.map((d, i) => (
          <span
            key={`${d.label}-${i}`}
            data-testid="donut-slice"
            data-label={d.label}
            data-value={d.value}
            data-show-label={d.value > 0 ? 'true' : 'false'}
          />
        ))}
      </div>
      <ResponsiveContainer width="100%" height={title ? height - 24 : height}>
        <PieChart>
          <Pie
            data={chartData}
            dataKey="value"
            nameKey="label"
            innerRadius="50%"
            outerRadius="80%"
            label={(entry) =>
              entry.value > 0 ? `${entry.label}: ${entry.value}` : null
            }
            isAnimationActive={false}
          >
            {chartData.map((entry, idx) => (
              <Cell
                key={`${entry.label}-${idx}`}
                fill={DEFAULT_COLORS[idx % DEFAULT_COLORS.length]}
              />
            ))}
          </Pie>
          <Tooltip />
        </PieChart>
      </ResponsiveContainer>
    </div>
  );
};

export default DonutChart;
